import { flexRender, type Table } from '@tanstack/react-table';
import { FilterX } from 'lucide-react';
import { cn } from '@/utils/style';
import { ColumnHeaderFilter } from './ColumnHeaderFilter';

interface TableHeaderProps<TData> {
  table: Table<TData>;
  isScrolling: boolean;
  data: TData[];
  /** Set when the table state differs from its defaults — renders the reset strip. */
  onResetFilters?: () => void;
}

export function TableHeader<TData>({
  table,
  isScrolling,
  data,
  onResetFilters,
}: TableHeaderProps<TData>) {
  const headerCount = table.getHeaderGroups()[0]?.headers.length ?? 1;

  return (
    <thead
      className={cn('sticky top-0 z-10 bg-gray-50', {
        "[&::after]:content-[''] [&::after]:absolute [&::after]:left-0 [&::after]:right-0 [&::after]:bottom-0 [&::after]:h-0.5 [&::after]:bg-gray-200 [&::after]:pointer-events-none":
          isScrolling,
      })}
    >
      {onResetFilters && (
        <tr>
          <th colSpan={headerCount} className="bg-gray-200 p-0">
            <div className="w-fit px-2 py-1 sticky left-0">
              <button
                type="button"
                onClick={onResetFilters}
                className="flex items-center gap-1.5 rounded px-2 py-0.5 text-xs font-normal text-gray-600 hover:bg-gray-100 hover:text-gray-900 cursor-pointer"
              >
                <FilterX className="size-3.5" />
                Réinitialiser les filtres
              </button>
            </div>
          </th>
        </tr>
      )}
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header, i) => (
            <th
              key={header.id}
              className={cn('text-primary text-left text-sm font-medium py-2 px-4 bg-gray-50', {
                'border-l-2 border-gray-200': i > 0,
              })}
            >
              <div className="flex items-center gap-1 text-xs">
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
                <ColumnHeaderFilter column={header.column} data={data} />
              </div>
            </th>
          ))}
        </tr>
      ))}
    </thead>
  );
}
